import type { ModuleRuntimeHooks } from 'vuetify-nuxt-module'
import { breakpoints } from '~/config/breakpoints'
import type { DeviceFamily } from '~/utils/device'

// The module types its hooks by augmenting RuntimeNuxtHooks through '#app', which
// has no effect in this Nuxt version (see utils/sessionChange.ts), so nuxtApp.hook
// does not know them; this is its signature for the module's own declarations.
type VuetifyHook = <Name extends keyof ModuleRuntimeHooks>(name: Name, callback: ModuleRuntimeHooks[Name]) => void

// Vuetify cannot measure the viewport on the server, so without a width it renders
// every page at xs and desktop visitors got the mobile layout until hydration. The
// server picks a width from the User-Agent guess and the client hydrates with the
// same one, so the markup matches; Vuetify measures the real viewport at
// app:suspense:resolve. Phones keep the unmeasured (xs) width they always had.
//
// The guess is wrong for some visitors (a desktop UA in a narrow window, iPadOS
// Safari's Mac UA), and then every breakpoint flag flips at that point. So a first
// render picks visible parts in one of these ways, not by a v-if on a flag:
// - switch them in CSS (hidden-*, media queries), as HeaderLayout does;
// - read useDeviceHint(), as ProductImage does;
// - read a flag once in setup, for a cutoff that lies between the guessed widths
//   below (md does), as AllFilters does.
// A v-if may still drop a part that is hidden at the measured width.
//
// Each width only has to land in the band its family is laid out in.
const SSR_WIDTH: Record<DeviceFamily, number | undefined> = {
  desktop: breakpoints.lg + 16, // lg and up: the desktop header and filter sidebar
  tablet: breakpoints.sm + 200, // sm, below md: the tablet layout, not the phone one
  mobile: undefined
}

export default defineNuxtPlugin({
  name: 'vuetify-ssr-display',
  // After the module's client-hints plugins (24 on the server) and before it creates
  // Vuetify (25): hooks run in the order they were added, so this one writes last.
  order: 24.5,
  setup (nuxtApp) {
    // A page the server did not render has no guess to match, so Vuetify measures.
    if (import.meta.client && !nuxtApp.payload.serverRendered) {
      return
    }
    const clientWidth = SSR_WIDTH[useDeviceHint().value]
    if (!clientWidth) {
      return
    }
    const hook = nuxtApp.hook as unknown as VuetifyHook
    hook('vuetify:before-create', ({ vuetifyOptions }) => {
      // A width the module read from the browser's client hints beats the guess.
      if (typeof vuetifyOptions.ssr === 'object' && vuetifyOptions.ssr.clientWidth) {
        return
      }
      vuetifyOptions.ssr = { clientWidth }
    })
  }
})
